import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useCallback, useRef, useState } from "react";
import { useModal } from "hooks/useModal";
import { useDrag, useDrop } from "react-dnd";
import {
  DEFAULT_EXPORT_ANALYSIS_TYPES,
  DEFAULT_GROUP_ORDER,
  DEFAULT_VISIBLE_SECTIONS,
  ExportAnalysisTypes,
  SECTION_GROUPS,
  SectionKey,
  SectionGroup,
  SECTION_RADIO_CONFIG,
} from "constants/financialSummary";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { BooksSettingLines } from "components/icons/Navbar/BooksSettingLines";
import { DragIcon } from "components/icons/DragIcon";
import { Export } from "components/icons/Export";
import ToolTip from "components/design/toolTip";

const DRAG_TYPE = "SECTION_GROUP";

type DraggableGroupProps = {
  group: SectionGroup;
  index: number;
  visibleSections: Partial<Record<SectionKey, boolean>>;
  onToggle: (key: SectionKey) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
};

const DraggableGroup = ({
  group,
  index,
  visibleSections,
  onToggle,
  onMove,
}: DraggableGroupProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLSpanElement>(null);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index]
  );

  const [, drop] = useDrop<{ index: number }>(
    () => ({
      accept: DRAG_TYPE,
      hover(item) {
        if (item.index === index) return;
        onMove(item.index, index);
        item.index = index;
      },
    }),
    [index, onMove]
  );

  preview(drop(ref));
  drag(dragHandleRef);

  return (
    <div ref={ref} className={isDragging ? "t-opacity-40" : "t-opacity-100"}>
      <div className="t-flex t-items-center t-gap-2 t-py-2">
        <span
          ref={dragHandleRef}
          className="t-cursor-grab t-text-neutral-40 t-select-none"
          title="Drag to reorder"
        >
          <DragIcon />
        </span>
        <span className="t-text-body-sm t-font-medium t-text-neutral-40 t-whitespace-nowrap">
          {group.label}
        </span>
        <div
          className="t-flex-1 t-h-px"
          style={{
            background: "linear-gradient(to right, #E2E8F0, transparent)",
          }}
        />
      </div>
      <div className="t-flex t-flex-col t-gap-4 t-pl-6 t-pb-3">
        {group.sections.map((section) => (
          <Checkbox
            key={section.key}
            name={section.key}
            label={section.label}
            checked={visibleSections[section.key]}
            onChange={() => onToggle(section.key)}
          />
        ))}
      </div>
    </div>
  );
};

type CustomizeSectionsTriggerProps = {
  visibleSections: Partial<Record<SectionKey, boolean>>;
  groupOrder: string[];
  onSave: (
    visibleSections: Partial<Record<SectionKey, boolean>>,
    groupOrder: string[]
  ) => void;
  sectionGroups?: SectionGroup[];
  defaultVisibleSections?: Partial<Record<SectionKey, boolean>>;
  defaultGroupOrder?: string[];
};

export const CustomizeSectionsTrigger = ({
  visibleSections,
  groupOrder,
  onSave,
  sectionGroups = SECTION_GROUPS,
  defaultVisibleSections = DEFAULT_VISIBLE_SECTIONS,
  defaultGroupOrder = DEFAULT_GROUP_ORDER,
}: CustomizeSectionsTriggerProps) => {
  const modal = useModal();

  return (
    <>
      <ToolTip text="Customize">
        <div>
          <Button customType="icon" size="xsmall" onClick={modal.open}>
            <span className="t-flex t-items-center t-gap-2 t-text-text-30">
              <BooksSettingLines color="currentcolor" />
            </span>
          </Button>
          <CustomizeSectionsPanel
            open={modal.isOpen}
            onClose={modal.close}
            visibleSections={visibleSections}
            groupOrder={groupOrder}
            onSave={onSave}
            sectionGroups={sectionGroups}
            defaultVisibleSections={defaultVisibleSections}
            defaultGroupOrder={defaultGroupOrder}
          />
        </div>
      </ToolTip>
    </>
  );
};

type CustomizeSectionsPanelProps = {
  open: boolean;
  onClose: () => void;
  visibleSections: Partial<Record<SectionKey, boolean>>;
  groupOrder: string[];
  onSave: (
    visibleSections: Partial<Record<SectionKey, boolean>>,
    groupOrder: string[]
  ) => void;
  sectionGroups: SectionGroup[];
  defaultVisibleSections: Partial<Record<SectionKey, boolean>>;
  defaultGroupOrder: string[];
};

const CustomizeSectionsPanel = ({
  open,
  onClose,
  visibleSections,
  groupOrder,
  onSave,
  sectionGroups,
  defaultVisibleSections,
  defaultGroupOrder,
}: CustomizeSectionsPanelProps) => {
  const [draftVisible, setDraftVisible] =
    useState<Partial<Record<SectionKey, boolean>>>(visibleSections);
  const [draftOrder, setDraftOrder] = useState<string[]>(groupOrder);

  const orderedGroups = draftOrder
    .map((key) => sectionGroups.find((g) => g.key === key))
    .filter(Boolean) as SectionGroup[];

  const handleToggle = (key: SectionKey) => {
    setDraftVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMove = useCallback((fromIndex: number, toIndex: number) => {
    setDraftOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const handleReset = () => {
    setDraftVisible(defaultVisibleSections);
    setDraftOrder(defaultGroupOrder);
  };

  const handleCancel = () => {
    setDraftVisible(visibleSections);
    setDraftOrder(groupOrder);
    onClose();
  };

  const handleSave = () => {
    onSave(draftVisible, draftOrder);
    onClose();
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleCancel()}
    >
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Customize financial summary</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-0">
          {orderedGroups.map((group, index) => (
            <DraggableGroup
              key={group.key}
              group={group}
              index={index}
              visibleSections={draftVisible}
              onToggle={handleToggle}
              onMove={handleMove}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <Button customType="ghost" size="small" onClick={handleReset}>
              Reset changes
            </Button>
            <div className="t-flex t-gap-2">
              <Button
                customType="secondary"
                size="small"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button customType="primary" size="small" onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};

type ExportSectionsModalProps = {
  open: boolean;
  onClose: () => void;
  visibleSections: Record<SectionKey, boolean>;
  groupOrder: string[];
  onExport: (
    sections: Record<SectionKey, boolean>,
    analysisTypes: ExportAnalysisTypes
  ) => void;
  analysisTypes?: ExportAnalysisTypes;
};

export const ExportSectionsModal = ({
  open,
  onClose,
  visibleSections,
  groupOrder,
  onExport,
  analysisTypes = DEFAULT_EXPORT_ANALYSIS_TYPES,
}: ExportSectionsModalProps) => {
  const [draftVisible, setDraftVisible] =
    useState<Record<SectionKey, boolean>>(visibleSections);
  const [draftAnalysisTypes, setDraftAnalysisTypes] =
    useState<ExportAnalysisTypes>(analysisTypes);

  const orderedGroups = groupOrder
    .map((key) => SECTION_GROUPS.find((g) => g.key === key))
    .filter(Boolean) as SectionGroup[];

  const handleToggle = (key: SectionKey) => {
    setDraftVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAnalysisTypeChange = (
    analysisTypeKey: keyof ExportAnalysisTypes,
    value: string
  ) => {
    setDraftAnalysisTypes((prev) => ({ ...prev, [analysisTypeKey]: value }));
  };

  return (
    <Modal.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Customize Financial Summary sections</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          {orderedGroups.map((group) => (
            <div key={group.key}>
              <div className="t-flex t-items-center t-gap-2 t-py-3">
                <span className="t-text-body-sm t-font-medium t-text-neutral-40 t-whitespace-nowrap">
                  {group.label}
                </span>
                <div className="t-flex-1 t-h-px t-bg-gradient-to-r t-from-neutral-10 t-to-transparent" />
              </div>
              <div className="t-flex t-flex-col t-gap-4 t-pl-6 t-pb-3">
                {group.sections.map((section) => {
                  const radioConfig = SECTION_RADIO_CONFIG[section.key];

                  return (
                    <div
                      key={section.key}
                      className="t-flex t-flex-col t-gap-2"
                    >
                      <Checkbox
                        name={section.key}
                        label={section.label}
                        checked={draftVisible[section.key]}
                        onChange={() => handleToggle(section.key)}
                      />
                      {radioConfig && draftVisible[section.key] && (
                        <div className="t-pl-6">
                          <Radio.Root
                            value={
                              draftAnalysisTypes[radioConfig.analysisTypeKey]
                            }
                            onValueChange={(value) =>
                              handleAnalysisTypeChange(
                                radioConfig.analysisTypeKey,
                                value
                              )
                            }
                          >
                            <Radio.Content>
                              <div className="t-flex t-flex-row t-gap-4">
                                {radioConfig.options.map((opt) => (
                                  <Radio.Item
                                    key={opt.value}
                                    value={opt.value}
                                    className="!t-w-auto"
                                  >
                                    <span className="t-text-body-sm t-text-text-100">
                                      {opt.label}
                                    </span>
                                  </Radio.Item>
                                ))}
                              </div>
                            </Radio.Content>
                          </Radio.Root>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button customType="secondary" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            customType="primary"
            size="small"
            onClick={() => onExport(draftVisible, draftAnalysisTypes)}
          >
            Save
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
